// Zipcode-area grouping colors for delivery screens (DeliveryBoardPage today; built as
// a lib so /driver can reuse the same cue later). See tasks/todo.md §0a for the design.
//
// Bundling happens at neighborhood level, so grouping is on the 4-digit postcode area
// ("9711 AB" → "9711") — full 6-char postcodes would almost never collide and give no
// signal. Colors are a scanning aid, never the only discriminator: the chip always shows
// the area number, so a palette collision between two areas is acceptable.

/** "9711 AB" / "9711ab" / " 9711 " → "9711"; null when there is no 4-digit prefix. */
export const zipcodeArea = (postcode?: string | null): string | null => {
  const match = (postcode ?? '').replace(/\s/g, '').match(/^\d{4}/)
  return match ? match[0] : null
}

export interface ZipcodeColor {
  /** Tailwind background class — full literal, JIT-safe. */
  bg: string
  /** Tailwind text class paired for contrast on that background. */
  text: string
}

// 25 visually distinct entries. Dark backgrounds carry white text; the handful of light
// ones carry near-black. Full class literals only — Tailwind's scanner cannot see
// computed class names.
const PALETTE: ZipcodeColor[] = [
  { bg: 'bg-red-600', text: 'text-white' },
  { bg: 'bg-orange-500', text: 'text-white' },
  { bg: 'bg-amber-400', text: 'text-gray-900' },
  { bg: 'bg-yellow-300', text: 'text-gray-900' },
  { bg: 'bg-lime-500', text: 'text-gray-900' },
  { bg: 'bg-green-600', text: 'text-white' },
  { bg: 'bg-emerald-500', text: 'text-white' },
  { bg: 'bg-teal-600', text: 'text-white' },
  { bg: 'bg-cyan-500', text: 'text-gray-900' },
  { bg: 'bg-sky-400', text: 'text-gray-900' },
  { bg: 'bg-blue-600', text: 'text-white' },
  { bg: 'bg-indigo-600', text: 'text-white' },
  { bg: 'bg-violet-500', text: 'text-white' },
  { bg: 'bg-purple-600', text: 'text-white' },
  { bg: 'bg-fuchsia-500', text: 'text-white' },
  { bg: 'bg-pink-500', text: 'text-white' },
  { bg: 'bg-rose-400', text: 'text-gray-900' },
  { bg: 'bg-red-900', text: 'text-white' },
  { bg: 'bg-orange-800', text: 'text-white' },
  { bg: 'bg-green-900', text: 'text-white' },
  { bg: 'bg-teal-900', text: 'text-white' },
  { bg: 'bg-blue-900', text: 'text-white' },
  { bg: 'bg-purple-900', text: 'text-white' },
  { bg: 'bg-pink-800', text: 'text-white' },
  { bg: 'bg-slate-700', text: 'text-white' },
]

/** Chip look for an area that is NOT part of a bundleable group — quiet on purpose. */
export const NEUTRAL_ZIPCODE_COLOR: ZipcodeColor = { bg: 'bg-gray-200', text: 'text-gray-600' }

// djb2 — deterministic, so "9711" maps to the same palette slot every day on every
// terminal with zero stored state.
const hash = (s: string): number => {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0
  return h
}

export const zipcodeColor = (area: string): ZipcodeColor => PALETTE[hash(area) % PALETTE.length]
